import { useTarget } from "lib/@hooks/use-target"
import { useDisabledUntilInvalidation } from "lib/@hooks/use-disabled-until-invalidation"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { useDialog } from "lib/@hooks/useDialog"
import { TargetGuard } from "lib/@components/target-guard"
import { EditRecord } from "lib/@components/edit-record"
import { LegacyInnerSettings } from "routes/asset-register/plugins/asset-mapping/legacy-inner-settings"
import { Box, Button, Card, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { applyMappingSettings } from "routes/asset-register/plugins/asset-mapping/controller/apply-mapping-settings"
import { rescanMapping } from "routes/asset-register/plugins/asset-mapping/controller/rescan-mapping"
import { updateAssetRegister } from "routes/asset-register/controller/update-asset-register"
import { Bound } from "lib/@components/binding/Bound"
import { useRef } from "react"
import { TreeToolbar } from "slot-definitions"
import { ListItemBox } from "lib/@components/ListItemBox"
import { joinList } from "lib/join-list"
import { LEGACY_LAWS, RULES } from "routes/asset-register/plugins/asset-mapping/constants"
import { Feature } from "lib/@components/feature"
import { useIsAnythingBeingMapped } from "routes/asset-register/plugins/asset-mapping/use-is-anything-being-mapped"
import { useIsAnythingRefining } from "routes/asset-register/plugins/asset-mapping/use-is-anything-refining"
import { FEATURE_FLAG_REFRESH_ALL_MAPPINGS } from "routes/asset-register/plugins/asset-mapping/feature-flag-refresh-all-mappings.runtime"
import { OpenMappingSettings } from "routes/asset-register/plugins/asset-mapping/settings-dialog"

export function LegacySettingsDialog() {
    const isMapping = useIsAnythingBeingMapped()
    const isRefining = useIsAnythingRefining()
    const currentOptions = useRef()
    const target = useTarget()
    const [disabled, disable] = useDisabledUntilInvalidation("typeFromRegister")
    const [, setHasConfiguredSettings] = useBoundValue("options.hasConfiguredSettings")
    const [options] = useBoundValue("options", {})

    const laws = Object.entries(options)
        .filter(([, v]) => !!v)
        .map(([k]) => LEGACY_LAWS[k])
        .filter(Boolean)

    const refreshAllButton = (
        <Feature feature={FEATURE_FLAG_REFRESH_ALL_MAPPINGS}>
            <Button
                disabled={isMapping || isRefining || disabled}
                size="small"
                startIcon={<Iconify icon="mdi:refresh" />}
                onClick={refreshAll}
                data-cy="refresh-all-mappings-button"
            >
                Refresh All Mappings
            </Button>
        </Feature>
    )

    TreeToolbar.usePlug(refreshAllButton, [isMapping, isRefining, disabled])

    const showSettings = useDialog(
        <TargetGuard notEmpty>
            <EditRecord field="options" store={storeOptions} initialise>
                <Bound saveSettings={saveSettings}>
                    <LegacyInnerSettings />
                </Bound>
            </EditRecord>
        </TargetGuard>,
        { maxWidth: "md" }
    )

    OpenMappingSettings.useEvent(() => {
        showSettings()
    })

    return (
        <>
            <Card elevation={0} raised={false}>
                <Box sx={{ p: 1 }}>
                    <ListItemBox spacing={1}>
                        <Box lineHeight={0}>
                            <Iconify color={"primary.main"} icon={RULES[options.mappingRule ?? "strict"].icon} />
                        </Box>
                        <Typography variant="body2">{RULES[options.mappingRule ?? "strict"].label}</Typography>
                        <Typography variant="body2" color={"text.secondary"}>
                            {laws.length > 0 && joinList(laws)}
                        </Typography>
                        <Box flex={1} />
                        <Button
                            disabled={isMapping}
                            size="small"
                            color="primary"
                            onClick={showSettings}
                            data-cy="mapping-settings-tab"
                        >
                            Change
                        </Button>
                    </ListItemBox>
                </Box>
            </Card>
        </>
    )

    async function refreshAll() {
        disable()
        await rescanMapping(target._id, ["all"])
    }

    async function saveSettings(editedOptions) {
        let saved = true
        // FIQ-1554: prefer the options handed over by Apply Settings - the debounced
        // EditRecord store may not have flushed into currentOptions yet
        const options = editedOptions ?? currentOptions.current?.options
        if (options) {
            try {
                await updateAssetRegister({
                    registerId: currentOptions.current?.registerId ?? target._id,
                    options: { ...options, hasConfiguredSettings: true },
                })
                currentOptions.current = null
            } catch (e) {
                saved = false
                console.error(e.message)
            }
        }
        setHasConfiguredSettings(true)
        if (saved) {
            applyMappingSettings(target._id).catch(console.error)
        }
    }

    async function storeOptions(options, registerId) {
        currentOptions.current = { options, registerId }
    }
}
